import { useCallback } from 'react';

import { rage } from '~/rage';

import { useLayoutDispatch, useLayoutSelector } from '../redux/hooks';
import {
	clearAll,
	setCode,
	setError,
	setIsExecuting,
	setResult,
} from '../redux/slices/runcodeSlice';

interface UseRuncodeStateReturn {
	code: string;
	result: string;
	error: string | null;
	isExecuting: boolean;
	handleCodeChange: (value: string) => void;
	handleExecuteClient: () => Promise<void>;
	handleExecuteServer: () => Promise<void>;
	handleClear: () => void;
}

export const useRuncodeState = (): UseRuncodeStateReturn => {
	const dispatch = useLayoutDispatch();
	const { code, result, error, isExecuting } = useLayoutSelector(
		(state) => state.runcode,
	);

	const executeCode = useCallback(
		async (target: 'client' | 'server') => {
			if (isExecuting) return;

			dispatch(setIsExecuting(true));
			dispatch(setError(null));
			dispatch(setResult(''));

			try {
				const response = await rage.runcode.executeCode(code, target);

				if (response.error) {
					dispatch(setError(response.error));
				} else {
					dispatch(setResult(response.result ?? ''));
				}
			} catch (error) {
				dispatch(
					setError(error instanceof Error ? error.message : String(error)),
				);
			} finally {
				dispatch(setIsExecuting(false));
			}
		},
		[code, isExecuting, dispatch],
	);

	const handleCodeChange = useCallback(
		(value: string) => {
			dispatch(setCode(value));
		},
		[dispatch],
	);

	const handleExecuteClient = useCallback(
		() => executeCode('client'),
		[executeCode],
	);

	const handleExecuteServer = useCallback(
		() => executeCode('server'),
		[executeCode],
	);

	const handleClear = useCallback(() => {
		dispatch(clearAll());
	}, [dispatch]);

	return {
		code,
		result,
		error,
		isExecuting,
		handleCodeChange,
		handleExecuteClient,
		handleExecuteServer,
		handleClear,
	};
};
